import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MapPin, BedDouble, Bath, Users, Maximize2, Wifi, Car, Laptop, Snowflake, WashingMachine, CookingPot, ArrowUpDown, Tv, Flame, Sun, Building2, User, Images } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Calendar } from "@/components/ui/calendar";
import BookingForm from "@/components/BookingForm";
import { FavButton } from "@/components/ListingCard";
import { useLang } from "@/lib/i18n";
import { api, eur, photoUrl } from "@/lib/api";

export const AMENITY_ICONS = { wifi: Wifi, parking: Car, desk: Laptop, ac: Snowflake, washer: WashingMachine, kitchen: CookingPot, elevator: ArrowUpDown, tv: Tv, heating: Flame, balcony: Sun };

function Gallery({ photos, title }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const Slides = ({ cls }) => (
    <Carousel className="w-full">
      <CarouselContent>
        {photos.map((p, i) => (
          <CarouselItem key={i}><img src={photoUrl(p)} alt={`${title} ${i + 1}`} className={`w-full object-cover ${cls}`} /></CarouselItem>
        ))}
      </CarouselContent>
      {photos.length > 1 && <><CarouselPrevious className="left-3" /><CarouselNext className="right-3" /></>}
    </Carousel>
  );
  return (
    <>
      <div className="md:hidden -mx-4 sm:-mx-6" data-testid="gallery-mobile"><Slides cls="aspect-[4/3]" /></div>
      <div className="hidden md:grid grid-cols-4 grid-rows-2 gap-2 h-[460px] rounded-3xl overflow-hidden relative" data-testid="gallery-desktop">
        {photos.slice(0, 5).map((p, i) => (
          <button key={i} onClick={() => setOpen(true)} className={`${i === 0 ? "col-span-2 row-span-2" : ""} overflow-hidden bg-slate-100`}>
            <img src={photoUrl(p)} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
          </button>
        ))}
        <button onClick={() => setOpen(true)} className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#142B5C] shadow" data-testid="gallery-show-all-btn"><Images className="h-4 w-4" />{t("show_photos")} ({photos.length})</button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-5xl p-2 bg-black border-0">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <Slides cls="max-h-[80vh] object-contain" />
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { t, pick } = useLang();
  const [l, setL] = useState(null);
  const [err, setErr] = useState(false);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { api.get(`/listings/${id}`).then((r) => setL(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="p-16 text-center text-slate-500" data-testid="listing-not-found">404</div>;
  if (!l) return <div className="p-16 text-center text-slate-400">{t("loading")}</div>;
  const initial = { check_in: sp.get("check_in") || "", check_out: sp.get("check_out") || "", guests: sp.get("guests") || 1 };
  const booked = l.booked.map((r) => ({ from: new Date(r.from + "T00:00:00"), to: new Date(r.to + "T00:00:00") }));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const facts = [[BedDouble, `${l.bedrooms} ${t("bedrooms")}`], [Bath, `${l.bathrooms} ${t("bathrooms").toLowerCase()}`], [Users, `${l.max_guests} ${t("guests_n")}`], [Maximize2, `${l.size_sqm} ${t("sqm")}`]];
  const price = (
    <div><span className="font-display text-3xl font-extrabold text-[#0B192C]" data-testid="detail-price">{eur(l.price_month_public)}</span> <span className="text-slate-500">{t("per_month")}</span>
      <p className="text-xs font-semibold text-[#FF6B00]">{l.min_months > 1 ? t("min_stay", { n: l.min_months }) : t("min_stay_1")}</p></div>
  );
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-28 lg:pb-16 md:pt-8" data-testid="listing-detail-page">
      <Gallery photos={l.photos.length ? l.photos : [{ url: "" }]} title={l.title} />
      <div className="mt-6 grid gap-10 lg:grid-cols-[1fr_400px]">
        <div className="space-y-10">
          <div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-1 text-sm font-semibold text-slate-500"><MapPin className="h-4 w-4 text-[#FF6B00]" />{l.city}{l.zone ? ` · ${l.zone}` : ""}</p>
                <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0B192C]" data-testid="detail-title">{pick(l, "title")}</h1>
              </div>
              <FavButton listingId={l.listing_id} className="border border-slate-200 shrink-0" />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#142B5C] px-3 py-1.5 text-xs font-bold text-white" data-testid="detail-host">
                {l.host?.type === "agency" ? <Building2 className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}{l.host?.type === "agency" ? t("host_agency") : t("host_owner")} · {l.host?.name}
              </span>
              <span className="rounded-full bg-orange-50 px-3 py-1.5 text-xs font-bold text-[#E05A00]">{t(`t_${l.property_type}`)}</span>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {facts.map(([I, s]) => <div key={s} className="flex items-center gap-2 rounded-xl bg-white border border-slate-200 p-3 text-sm font-semibold text-[#142B5C]"><I className="h-5 w-5 text-[#FF6B00]" />{s}</div>)}
            </div>
          </div>
          <section><h2 className="text-xl font-bold text-[#0B192C]">{t("description")}</h2><p className="mt-3 whitespace-pre-line text-slate-700 leading-relaxed" data-testid="detail-description">{pick(l, "description")}</p></section>
          <section>
            <h2 className="text-xl font-bold text-[#0B192C]">{t("services")}</h2>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="detail-amenities">
              {l.amenities.map((a) => { const I = AMENITY_ICONS[a] || Wifi; return <div key={a} className="flex items-center gap-2 text-sm text-slate-700"><I className="h-5 w-5 text-[#142B5C]" />{t(`a_${a}`)}</div>; })}
            </div>
          </section>
          {l.extras.length > 0 && (
            <section>
              <h2 className="text-xl font-bold text-[#0B192C]">{t("extras")}</h2>
              <p className="text-sm text-slate-500">{t("extras_note")}</p>
              <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="detail-extras">
                {l.extras.map((e) => <div key={e.extra_id} className="rounded-xl border border-orange-200 bg-orange-50/60 p-4"><p className="font-semibold text-[#142B5C]">{pick(e, "name")}</p><p className="text-sm text-slate-600">{eur(e.price)} {e.unit === "once" ? t("once") : t("monthly")}</p></div>)}
              </div>
            </section>
          )}
          <section>
            <h2 className="text-xl font-bold text-[#0B192C]">{t("availability")}</h2>
            <p className="text-sm text-slate-500">{t("availability_note")}</p>
            <div className="mt-4 inline-block rounded-2xl bg-white border border-slate-200 p-2" data-testid="detail-calendar">
              <Calendar mode="single" numberOfMonths={typeof window !== "undefined" && window.innerWidth >= 768 ? 2 : 1}
                disabled={[{ before: l.available_from ? new Date(Math.max(today, new Date(l.available_from + "T00:00:00"))) : today }, ...booked]}
                modifiers={{ booked }} modifiersClassNames={{ booked: "line-through !text-red-400" }} />
            </div>
          </section>
          <section>
            <h2 className="text-xl font-bold text-[#0B192C]">{t("location")}</h2>
            <div className="mt-3 flex items-center gap-3 rounded-2xl bg-white border border-slate-200 p-5"><MapPin className="h-6 w-6 text-[#FF6B00]" /><div><p className="font-semibold text-[#142B5C]">{l.zone ? `${l.zone}, ` : ""}{l.city}</p><p className="text-sm text-slate-500">{t("location_note")}</p></div></div>
          </section>
        </div>
        <aside className="hidden lg:block">
          <div className="sticky top-28 rounded-3xl bg-white border border-slate-200 p-6 shadow-sm space-y-5" data-testid="booking-panel">
            {price}
            <BookingForm listing={l} initial={initial} />
          </div>
        </aside>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-3" data-testid="mobile-booking-bar">
        <div className="min-w-0"><p className="whitespace-nowrap"><span className="font-display text-2xl font-extrabold text-[#0B192C]">{eur(l.price_month_public)}</span> <span className="text-sm text-slate-500">{t("per_month")}</span></p>
          <p className="text-xs font-semibold text-[#FF6B00]">{l.min_months > 1 ? t("min_stay", { n: l.min_months }) : t("min_stay_1")}</p></div>
        <Drawer open={drawer} onOpenChange={setDrawer}>
          <DrawerTrigger asChild><button className="h-12 shrink-0 whitespace-nowrap rounded-full bg-[#FF6B00] px-5 font-bold text-white" data-testid="mobile-request-btn">{t("request_booking")}</button></DrawerTrigger>
          <DrawerContent aria-describedby={undefined} className="max-h-[92vh]">
            <DrawerTitle className="px-5 pt-4 font-display text-lg">{t("booking_title")}</DrawerTitle>
            <div className="overflow-y-auto p-5"><BookingForm listing={l} initial={initial} onDone={() => setDrawer(false)} /></div>
          </DrawerContent>
        </Drawer>
      </div>
    </div>
  );
}
